import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { MapPin, Calendar, Search, Share2, HeartHandshake, Sparkles } from "lucide-react";
import { toast } from "sonner";

const milesBetween = (a, b) => {
  const R = 3958.8, dLat = ((b.lat - a.lat) * Math.PI) / 180, dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const s = Math.sin(dLat / 2) ** 2 + Math.cos((a.lat * Math.PI) / 180) * Math.cos((b.lat * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
};

const fmtDay = (d) => {
  if (!d) return "Flexible";
  try { return new Date(`${d}T12:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "short" }); } catch { return d; }
};

function EventCard({ e, near }) {
  const href = e.kind === "gathering" ? `/collectives/${e.collective_id}` : `/opportunities/${e.id}`;
  return (
    <Link
      to={href}
      data-testid={`campaign-event-${e.id}`}
      className="block bg-white border border-neutral-200 rounded-xl p-4 hover:border-terracotta/50 transition-colors"
    >
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.14em] font-bold text-terracotta">
        {e.is_help_request ? <HeartHandshake className="w-3.5 h-3.5" /> : <Sparkles className="w-3.5 h-3.5" />}
        {e.kind === "gathering" ? "Gathering" : e.is_help_request ? "Neighbour needs a hand" : "Opportunity"}
      </div>
      <div className="font-medium text-forest mt-1.5">{e.title}</div>
      <div className="text-xs text-neutral-500 mt-1 flex items-center gap-3 flex-wrap">
        <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {fmtDay(e.date)}</span>
        <span className="inline-flex items-center gap-1 truncate"><MapPin className="w-3 h-3" /> {e.location}</span>
        {near != null && <span className="text-forest font-medium">{near.toFixed(1)} mi away</span>}
      </div>
    </Link>
  );
}

// Phase 18-A2 item 3: public campaign landing — hero, events grouped by
// launch area, postcode search, two CTAs. No national map.
export default function CampaignLanding() {
  const { slug } = useParams();
  const [d, setD] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [postcode, setPostcode] = useState("");
  const [origin, setOrigin] = useState(null);

  useEffect(() => {
    api.campaignLanding(slug).then(setD).catch(() => setNotFound(true));
  }, [slug]);

  const searchPostcode = async (e) => {
    e.preventDefault();
    if (!postcode.trim()) { setOrigin(null); return; }
    try {
      const hits = await api.geoSearch(postcode.trim());
      if (hits?.length) {
        setOrigin({ lat: hits[0].lat, lng: hits[0].lng, label: hits[0].label });
      } else {
        toast.error("We couldn't find that place — try a UK postcode or town");
      }
    } catch { toast.error("Search failed — try again"); }
  };

  const share = async () => {
    const url = `${window.location.origin}/campaigns/${slug}`;
    if (navigator.share) {
      try { await navigator.share({ title: d.campaign.name, url }); return; } catch { /* fallthrough */ }
    }
    navigator.clipboard.writeText(url).then(() => toast.success("Link copied")).catch(() => {});
  };

  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="campaign-not-found">
        <div className="font-display text-3xl text-forest">Campaign not found</div>
        <Link to="/opportunities" className="inline-block mt-4 text-forest underline underline-offset-4">Browse opportunities instead</Link>
      </div>
    );
  }
  if (!d) return <div className="max-w-5xl mx-auto px-6 py-24 text-neutral-400">Loading…</div>;

  const { campaign: c, ended, groups } = d;
  const allEvents = groups.flatMap((g) => g.events);
  const nearest = origin
    ? allEvents
        .filter((e) => e.lat != null && e.lng != null)
        .map((e) => ({ ...e, _mi: milesBetween(origin, e) }))
        .sort((a, b) => a._mi - b._mi)
        .slice(0, 6)
    : null;

  return (
    <div className="max-w-5xl mx-auto px-6 py-14" data-testid="campaign-landing">
      <div className="text-xs uppercase tracking-[0.2em] text-terracotta font-bold">A moment to join in</div>
      <h1 className="font-display text-4xl sm:text-5xl text-forest mt-3 tracking-tight" data-testid="campaign-name">{c.name}</h1>
      <div className="text-terracotta font-medium mt-2" data-testid="campaign-dates">
        {fmtDay(c.starts_on)} – {fmtDay(c.ends_on)}{ended && <span className="text-neutral-500"> · this campaign has ended</span>}
      </div>
      {c.blurb && <p className="text-neutral-600 mt-4 max-w-2xl">{c.blurb}</p>}

      <div className="flex flex-wrap gap-2 mt-6">
        <a href="#events">
          <Button data-testid="campaign-cta-find" className="rounded-full bg-forest hover:bg-forest-hover h-11 px-6">Find one near you</Button>
        </a>
        {!ended && (
          <Link to={`/post-opportunity?campaign=${c.slug}`}>
            <Button data-testid="campaign-cta-host" variant="outline" className="rounded-full h-11 px-6 border-forest/30 text-forest hover:bg-forest/5">
              Host one on your street
            </Button>
          </Link>
        )}
        <Button onClick={share} variant="outline" className="rounded-full h-11 px-4" data-testid="campaign-share-btn">
          <Share2 className="w-4 h-4 mr-1.5" /> Share
        </Button>
      </div>

      <form onSubmit={searchPostcode} className="mt-10 flex gap-2 max-w-md" id="events">
        <Input
          data-testid="campaign-postcode-input"
          value={postcode}
          onChange={(e) => setPostcode(e.target.value)}
          placeholder="Your postcode or town"
          className="h-11"
        />
        <Button type="submit" variant="outline" className="rounded-full h-11 px-5" data-testid="campaign-postcode-search">
          <Search className="w-4 h-4" />
        </Button>
      </form>

      {nearest && (
        <div className="mt-8" data-testid="campaign-nearest">
          <h2 className="font-display text-lg text-forest">Closest to {origin.label}</h2>
          <div className="grid sm:grid-cols-2 gap-3 mt-3">
            {nearest.map((e) => <EventCard key={`${e.kind}-${e.id}`} e={e} near={e._mi} />)}
          </div>
          {nearest.length === 0 && <p className="text-sm text-neutral-500 mt-2">Nothing mapped near there yet — check the areas below.</p>}
        </div>
      )}

      {groups.length === 0 ? (
        <p className="mt-10 text-neutral-500" data-testid="campaign-no-events">
          {ended ? "The events have wrapped up — thank you to everyone who joined in." : "Events are being added — check back soon, or be the first to host one."}
        </p>
      ) : (
        groups.map((g) => (
          <div key={g.area} className="mt-10" data-testid={`campaign-area-${g.area.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <h2 className="font-display text-xl text-forest">{g.area}</h2>
            <div className="grid sm:grid-cols-2 gap-3 mt-3">
              {g.events.map((e) => <EventCard key={`${e.kind}-${e.id}`} e={e} />)}
            </div>
          </div>
        ))
      )}
    </div>
  );
}
